/*
 * Pure helpers for the brand detail page: everything shown in the summary line,
 * the meta tags, the "About" section, the FAQ and the Product JSON-LD is derived
 * here from the brand record. Nothing is invented: a field that is missing is
 * simply left out.
 */

/** Product types that have their own wording in the `brand` / `brands` messages. */
const COPY_TYPES = ['giftcard', 'topup', 'pin', 'data', 'bundle', 'digitaltv', 'longdistance'];
const DELIVERY_TYPES = ['email', 'phone', 'account'];
// Mirrors the minimum enforced by the buy box (Brand#validAmount).
const CLAMPED_CURRENCIES = ['CAD', 'EUR', 'AUD', 'GBP'];
const CLAMPED_MIN_IN_CENTS = 500;

const GIFT_CARD_PATTERN = /gift\s?cards?/i;
// "$500 - $999" style ranges some brand names carry.
const PRICE_RANGE_PATTERN = /\$\s*\d+(?:,\d{3})*\s*[-–—]\s*\$\s*\d+(?:,\d{3})*/g;

/** Brand type as an ICU `select` key. Unknown types fall through to `other`. */
export const typeKeyForBrand = (brand) => (COPY_TYPES.includes(brand?.type) ? brand.type : 'other');

/**
 * Like `typeKeyForBrand`, for messages that append "gift card" to the name:
 * a brand already called "… Gift Card" uses the plain (`other`) form.
 */
export const headingKeyForBrand = (brand) => {
  const key = typeKeyForBrand(brand);

  if (key === 'giftcard' && GIFT_CARD_PATTERN.test(brand.name || '')) {
    return 'other';
  }

  return key;
};

/** Delivery type of the first product as an ICU `select` key. */
export const deliveryKeyForBrand = (brand) => {
  const deliveryType = brand?.products?.[0]?.deliveryType;

  return DELIVERY_TYPES.includes(deliveryType) ? deliveryType : 'other';
};

/** Brand name without an embedded price range ("Visa $500 - $999" becomes "Visa"). */
export const cleanBrandName = (name = '') =>
  name.replace(PRICE_RANGE_PATTERN, '').replace(/ {2,}/g, ' ').trim();

const toCents = (value) => {
  const number = Number(value);

  return Number.isFinite(number) && number > 0 ? number : null;
};

const fixedAmountsForProduct = (product) => {
  const allowed = (product.allowedPricesInCents || []).map(toCents).filter(Boolean);

  if (allowed.length > 0) {
    return allowed;
  }

  const min = toCents(product.minPriceInCents);

  return min ? [min] : [];
};

const rangeForProduct = (product) => {
  let low = toCents(product.minPriceInCents);
  const high = toCents(product.maxPriceInCents);

  if (
    low &&
    product.currency === product.quoteCurrency &&
    CLAMPED_CURRENCIES.includes(product.currency)
  ) {
    low = Math.max(low, CLAMPED_MIN_IN_CENTS);
  }

  return { low: low || high, high: high || low };
};

/**
 * What the brand can be bought for, in the currency of its first product.
 *
 * Returns null when the products carry no usable price, otherwise:
 *   currency     ISO code of the product currency
 *   lowInCents   smallest amount
 *   highInCents  largest amount
 *   amounts      sorted, de-duplicated fixed denominations (cents); empty when
 *                any product takes a free amount
 *   isRange      true when the buyer picks an amount between low and high
 *   increment    step of a free amount in whole currency units, when above 1
 *   offerCount   number of products counted
 */
export const getPriceSummary = (products) => {
  if (!Array.isArray(products) || products.length === 0) {
    return null;
  }

  const currency = products[0].currency;

  if (!currency) {
    return null;
  }

  const sameCurrency = products.filter((product) => product.currency === currency);
  const fixed = new Set();
  let low = Number.POSITIVE_INFINITY;
  let high = 0;
  let isRange = false;
  let increment = null;
  let offerCount = 0;

  sameCurrency.forEach((product) => {
    if (product.variablePrice) {
      const range = rangeForProduct(product);

      if (!range.low || !range.high) return;

      isRange = true;
      low = Math.min(low, range.low);
      high = Math.max(high, range.high);
      offerCount += 1;

      const step = Number(product.increment);
      if (Number.isFinite(step) && step > 1) {
        increment = step;
      }
    } else {
      const amounts = fixedAmountsForProduct(product);

      if (amounts.length === 0) return;

      amounts.forEach((amount) => {
        fixed.add(amount);
        low = Math.min(low, amount);
        high = Math.max(high, amount);
      });
      offerCount += 1;
    }
  });

  if (offerCount === 0) {
    return null;
  }

  return {
    currency,
    lowInCents: low,
    highInCents: high,
    amounts: isRange ? [] : [...fixed].sort((a, b) => a - b),
    isRange,
    increment: isRange ? increment : null,
    offerCount,
  };
};

/** "$25", "CA$25", "€12.50": an amount in cents in the product currency. */
export const formatMoney = (amountInCents, currency, locale) => {
  const amount = amountInCents / 100;

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (_error) {
    return `${amount} ${currency}`;
  }
};

/** "British Pound (GBP)", or just the code when the runtime has no name for it. */
export const currencyLabel = (currency, locale) => {
  try {
    const name = new Intl.DisplayNames([locale], { type: 'currency' }).of(currency);

    return name && name !== currency ? `${name} (${currency})` : currency;
  } catch (_error) {
    return currency;
  }
};

/** Whole days in an ISO 8601 duration such as "P30D", "P1M" or "P1Y"; null when absent or unparseable. */
export const validityInDays = (iso) => {
  if (!iso || typeof iso !== 'string') {
    return null;
  }

  const match = iso.match(/^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)W)?(?:(\d+)D)?(?:T.*)?$/);

  if (!match) {
    return null;
  }

  const [, years = 0, months = 0, weeks = 0, days = 0] = match;
  const total = Number(years) * 365 + Number(months) * 30 + Number(weeks) * 7 + Number(days);

  return total > 0 ? total : null;
};

/** The single validity period shared by every product, in days; null when there is none or they differ. */
export const sharedValidityInDays = (products) => {
  if (!Array.isArray(products) || products.length === 0) {
    return null;
  }

  const periods = new Set(products.map((product) => validityInDays(product.validityPeriodIso)));

  if (periods.size !== 1) {
    return null;
  }

  return [...periods][0];
};

/**
 * Products worth listing by name: only when there are several and their names
 * say more than the brand name does. De-duplicated by name and amount.
 */
export const getNamedProducts = (brand) => {
  const products = brand?.products;

  if (!Array.isArray(products) || products.length < 2) {
    return [];
  }

  const seen = new Set();

  return products
    .filter((product) => product.name && product.name !== brand.name)
    .map((product) => {
      const [amountInCents] = product.variablePrice ? [] : fixedAmountsForProduct(product);

      return {
        id: product.id,
        name: product.name,
        currency: product.currency,
        amountInCents: amountInCents || null,
        validityDays: validityInDays(product.validityPeriodIso),
      };
    })
    .filter((product) => {
      const key = `${product.name}:${product.amountInCents}`;

      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => (a.amountInCents || 0) - (b.amountInCents || 0));
};

/** Single-line plain text, cut at a word boundary. Used for meta and JSON-LD only. */
export const toPlainText = (text, maxLength = 300) => {
  if (!text || typeof text !== 'string') {
    return '';
  }

  const plain = text
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (plain.length <= maxLength) {
    return plain;
  }

  const cut = plain.slice(0, maxLength - 1);
  const lastSpace = cut.lastIndexOf(' ');

  return `${(lastSpace > maxLength * 0.6 ? cut.slice(0, lastSpace) : cut).replace(/[\s.,;:!?-]+$/, '')}…`;
};

/** True when the brand can be bought right now (same rule as the buy box). */
export const isPurchasable = (brand) =>
  !!brand?.products &&
  !!brand.enabled &&
  !brand.discontinued &&
  !brand.thirdPartyDisabled &&
  !!brand.country?.enabled &&
  !brand.isOutOfStock;

/**
 * The breadcrumb trail: country, first category (when there is one), brand.
 * `href` is a site path without locale prefix; the current page has none.
 * The visible breadcrumb and the BreadcrumbList JSON-LD are both built from this.
 */
export const getBreadcrumbItems = (brand) => {
  const { country } = brand;
  const firstCategory = brand.categories?.[0];
  const items = [{ name: country.name, href: `/buy/${country.slug}` }];

  if (firstCategory?.slug) {
    items.push({ name: firstCategory.name, href: `/buy/${country.slug}/${firstCategory.slug}` });
  }

  items.push({ name: brand.name, href: null });

  return items;
};
